/**
 * Rotate Webhook Secret Modal (September 2025)
 *
 * Modal for rotating webhook signing secrets with one-time secret display.
 *
 * Features:
 * - Confirmation before rotation
 * - New secret display with copy-to-clipboard
 * - Security warnings and guidance
 * - Terminal-themed styling with Tailwind CSS
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useConvex } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type { RotateWebhookSecretResponse } from '@keypalace/shared-types';

interface Webhook {
  id: Id<'webhooks'>;
  name: string;
  url: string;
}

interface RotateSecretModalProps {
  isOpen: boolean;
  webhook: Webhook | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export function RotateSecretModal({
  isOpen,
  webhook,
  onClose,
  onSuccess,
}: RotateSecretModalProps) {
  const convex = useConvex();
  const queryClient = useQueryClient();
  const [newSecret, setNewSecret] = useState<string | null>(null);

  const rotateSecretMutation = useMutation({
    mutationFn: async (webhookId: Id<'webhooks'>) => {
      return await convex.mutation(api.webhooks.mutations.rotateWebhookSecret, {
        webhookId,
      });
    },
    onSuccess: (response: RotateWebhookSecretResponse) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setNewSecret(response.secret);
      if (onSuccess) {
        onSuccess();
      }
    },
  });

  const handleRotate = async () => {
    if (!webhook) return;
    try {
      await rotateSecretMutation.mutateAsync(webhook.id);
    } catch (error) {
      console.error('Failed to rotate webhook secret:', error);
    }
  };

  const handleCopySecret = async () => {
    if (newSecret) {
      await navigator.clipboard.writeText(newSecret);
    }
  };

  const handleClose = () => {
    setNewSecret(null);
    onClose();
  };

  if (!isOpen || !webhook) return null;

  if (newSecret) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-terminal-black/90 backdrop-blur-sm">
        <div className="terminal-box max-w-2xl w-full mx-4 p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-terminal-orange terminal-glow mb-2">
              [SECRET ROTATED SUCCESSFULLY]
            </h2>
            <p className="text-terminal-cyan text-sm">
              Save your new webhook signing secret - it will only be shown once!
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-terminal-cyan text-sm font-bold mb-2">
              NEW WEBHOOK SECRET (COPY THIS NOW)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newSecret}
                readOnly
                className="flex-1 bg-terminal-black border-2 border-terminal-cyan text-terminal-orange p-3 font-mono text-sm focus:outline-none focus:border-terminal-orange"
              />
              <button
                onClick={handleCopySecret}
                className="bg-terminal-cyan text-terminal-black font-bold px-6 border-2 border-terminal-cyan hover:bg-transparent hover:text-terminal-cyan transition-all duration-300"
              >
                COPY
              </button>
            </div>
            <p className="text-terminal-cyan/70 text-xs mt-2">
              {newSecret.startsWith('test_wh_') ? '⚠️ Test secret (development)' : '🔒 Production secret'}
            </p>
          </div>

          <div className="bg-terminal-black border-2 border-terminal-cyan p-4 mb-6">
            <h3 className="text-terminal-orange font-bold mb-2">[IMPORTANT]</h3>
            <ul className="text-terminal-cyan text-sm space-y-1 list-disc list-inside">
              <li>Update your application with the new secret immediately</li>
              <li>The old secret is now invalid and cannot be used</li>
              <li>Webhook deliveries will fail until you update the secret</li>
              <li>Test your webhook integration after updating</li>
            </ul>
          </div>

          <button
            onClick={handleClose}
            className="w-full bg-terminal-orange text-terminal-black font-bold py-3 px-6 border-2 border-terminal-orange hover:bg-transparent hover:text-terminal-orange transition-all duration-300 terminal-glow"
          >
            DONE - I'VE SAVED MY NEW SECRET
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-terminal-black/90 backdrop-blur-sm">
      <div className="terminal-box max-w-2xl w-full mx-4 p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-terminal-orange terminal-glow mb-2">
            [ROTATE WEBHOOK SECRET]
          </h2>
          <p className="text-terminal-cyan text-sm">
            Generate a new signing secret for this webhook endpoint.
          </p>
        </div>

        <div className="bg-terminal-black border-2 border-terminal-cyan p-6 mb-6">
          <h3 className="text-terminal-orange font-bold mb-4">[WEBHOOK DETAILS]</h3>
          <div className="space-y-2">
            <div>
              <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                Name
              </label>
              <div className="text-terminal-cyan text-lg font-semibold">
                {webhook.name}
              </div>
            </div>
            <div>
              <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                URL
              </label>
              <div className="text-terminal-cyan text-sm font-mono break-all">
                {webhook.url}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-terminal-orange/20 border-2 border-terminal-orange p-4 mb-6">
          <h3 className="text-terminal-orange font-bold mb-2">[IMPORTANT]</h3>
          <ul className="text-terminal-cyan text-sm space-y-1 list-disc list-inside">
            <li>A new signing secret will be generated</li>
            <li>The old secret will be immediately invalidated</li>
            <li>You must update your application with the new secret</li>
            <li>Webhook deliveries will fail until you update the secret</li>
            <li>The new secret will only be shown once</li>
          </ul>
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleClose}
            disabled={rotateSecretMutation.isPending}
            className="flex-1 bg-transparent text-terminal-cyan font-bold py-3 px-6 border-2 border-terminal-cyan hover:bg-terminal-cyan hover:text-terminal-black transition-all duration-300 disabled:opacity-50"
          >
            CANCEL
          </button>
          <button
            onClick={handleRotate}
            disabled={rotateSecretMutation.isPending}
            className="flex-1 bg-terminal-orange text-terminal-black font-bold py-3 px-6 border-2 border-terminal-orange hover:bg-transparent hover:text-terminal-orange transition-all duration-300 terminal-glow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {rotateSecretMutation.isPending ? 'ROTATING...' : 'ROTATE SECRET'}
          </button>
        </div>

        {rotateSecretMutation.isError && (
          <div className="mt-4 bg-red-900/20 border-2 border-red-400 p-4">
            <p className="text-red-400 text-sm font-bold">
              [ERROR] Failed to rotate webhook secret
            </p>
            <p className="text-red-400/70 text-xs mt-1">
              {(rotateSecretMutation.error as Error)?.message || 'Unknown error occurred'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
